import { z } from 'zod';
import { IdSchema, E164Schema, MoneySchema, TimestampSchema, ListQuerySchema, BoolSchema } from './common.js';

export const CallDirectionSchema = z.enum(['inbound', 'outbound', 'internal']);

export const CdrSchema = z.object({
  id: IdSchema,
  call_uuid: IdSchema,
  tenant_id: IdSchema,
  direction: CallDirectionSchema,
  leg: z.enum(['a', 'b']),
  from_number: z.string().nullable(),
  from_name: z.string().nullable(),
  to_number: z.string().nullable(),
  destination_e164: E164Schema.nullable(),
  extension_id: IdSchema.nullable(),
  started_at: TimestampSchema,
  answered_at: TimestampSchema.nullable(),
  ended_at: TimestampSchema,
  duration_sec: z.number().int(),
  billable_sec: z.number().int(),
  hangup_cause: z.string().nullable(),
  sip_code: z.number().int().nullable(),
  // Quality
  mos: z.string().nullable(),
  jitter_ms: z.string().nullable(),
  packet_loss_pct: z.string().nullable(),
  codec: z.string().nullable(),
  // Money -- strings, never numbers. See MoneySchema.
  currency: z.string().length(3).nullable(),
  sell_amount: MoneySchema.nullable(),
  /** Their own carrier's charge for a call over their own trunk, from their uploaded sheet (0131). */
  carrier_cost: MoneySchema.nullable().optional(),
  /** Platform and reseller scopes only; stripped for tenant scope in the handler. */
  cost_amount: MoneySchema.nullable().optional(),
  margin_amount: MoneySchema.nullable().optional(),
  has_recording: z.boolean(),
  /** Why the switch refused the call (0157); null when it did not. See refusalText. */
  refused: z.string().nullable().optional(),
  /** The seat that placed an outbound or internal call, by name (any of its registrations). */
  from_extension: z.object({ name: z.string(), number: z.string() }).nullable().optional(),
  /** The seat an internal call went to. */
  to_extension: z.object({ name: z.string(), number: z.string() }).nullable().optional(),
  /** What the agent said the call was (0108). Absent when nobody has said. */
  disposition: z.object({
    code: z.string(),
    label: z.string(),
    kind: z.enum(['positive', 'neutral', 'negative']),
    note: z.string().nullable(),
  }).nullable().optional(),
  /** The call's tags (0152), from the account's list. */
  tags: z.array(z.string()).nullable().optional(),
  /**
   * Whether a recorded call can be played: ready once its node reports the upload,
   * saving until then, unavailable if it never reached storage. Null when not recorded.
   */
  recording_status: z.enum(['ready', 'saving', 'unavailable']).nullable().optional(),
  /** The recording itself, when there is one: what it should hash to, what it is, how big. */
  recording: z.object({
    sha256: z.string().nullable(),
    source_sha256: z.string().nullable(),
    format: z.string(),
    bytes: z.number().nullable(),
  }).optional(),
});
export type Cdr = z.infer<typeof CdrSchema>;

/**
 * A refused call's reason in words (cdrs.refused, 0157), as the caller was told it: the dial
 * policy's (arc-rate dialpolicy.ts, and the gate's own) and the agent controls' (agent /fs/agentgate).
 */
const REFUSALS: Record<string, string> = {
  internal_only: 'Internal calls only',
  international_off: 'International calls are off',
  country_not_allowed: 'Calls to this country are not allowed',
  premium_blocked: 'Premium-rate numbers are blocked',
  blocked_prefix: 'This number is blocked',
  no_credit: 'Out of credit',
  fraud_hold: 'Calls paused by a fraud hold',
  over_budget: 'Over the spend limit',
  unavailable: 'The check could not be made',
  no_seat: 'Not placed from an extension',
  no_dial_out: 'Dialling out is off for this agent',
  no_consult: 'Transfers are off for this agent',
};
export const refusalText = (reason: string): string => REFUSALS[reason] ?? 'Not allowed';

/**
 * The direction to SHOW a call in: what was dialled, for a refused one. A refused call's record
 * says internal -- no outbound route stamped it, so it is never billed -- and a blocked call to
 * Namibia is not an internal call (owner, 6 October 2026). A number of seven digits or more is one
 * the dial gate treated as outside (fsxml.ts dialGateXml); the API's direction filter uses the
 * same rule (cdr/routes.ts).
 */
export function shownDirection(c: { direction: string; refused?: string | null | undefined; to_number?: string | null | undefined }): string {
  return c.direction === 'internal' && c.refused && /^\+?\d{7,}$/.test(c.to_number ?? '') ? 'outbound' : c.direction;
}

/**
 * Filterable fields are declared explicitly. Anything else is a 422 rather than
 * a silently ignored parameter, which is what stops an unindexed full scan
 * reaching the database (doc 05 section 3).
 */
export const CdrListQuerySchema = ListQuerySchema.extend({
  /**
   * A call the switch carries has two records: the caller's leg and the one towards the carrier.
   * Listing both reads as every call twice (owner, 20 September 2026), so only the caller's leg is
   * listed unless 'all' is asked for. Both are kept: cost and quality are per leg.
   */
  legs: z.enum(['main', 'all']).default('main'),
  'started_at[gte]': TimestampSchema.optional(),
  'started_at[lt]': TimestampSchema.optional(),
  direction: CallDirectionSchema.optional(),
  extension_id: IdSchema.optional(),
  'destination_e164[prefix]': z.string().max(20).optional(),
  // Not z.coerce.boolean(): that parses 'false' as true, so filtering for
  // UNANSWERED calls returned answered ones. See BoolSchema.
  answered: BoolSchema.optional(),
  /** Only calls that were recorded. */
  recorded: BoolSchema.optional(),
  q: z.string().max(64).optional(),
  sort: z.enum(['started_at', '-started_at', 'duration_sec', '-duration_sec']).default('-started_at'),
});
export type CdrListQuery = z.infer<typeof CdrListQuerySchema>;

export const CdrStatsSchema = z.object({
  window: z.object({ from: TimestampSchema, to: TimestampSchema }),
  total_calls: z.number().int(),
  answered_calls: z.number().int(),
  answer_rate_pct: z.number(),
  total_duration_sec: z.number().int(),
  avg_duration_sec: z.number(),
  inbound: z.number().int(),
  outbound: z.number().int(),
  internal: z.number().int(),
  spend: MoneySchema.nullable(),
  currency: z.string().length(3).nullable(),
});
export type CdrStats = z.infer<typeof CdrStatsSchema>;

/**
 * Every account in the reader's reach at once (the staff dashboard with "all accounts" chosen):
 * the same figures as CdrStats, summed, and one row per account.
 */
export interface Overview {
  window: { from: string; to: string };
  totals: CdrStats;
  extensions: { total: number; active: number };
  accounts: Array<{
    id: string; name: string; slug: string; status: string;
    calls: number; answered: number; spend: string | null; currency: string | null;
    extensions: number; active_extensions: number;
  }>;
}

/**
 * The platform dashboard's charts (GET /overview/insights, platform staff only). Days and hours
 * are in `window.timezone`. A section the role cannot read, or the node cannot answer, is null.
 */
export interface PlatformInsights {
  window: { from: string; days: number; timezone: string };
  series: Array<{
    day: string; inbound: number; outbound: number; internal: number; answered: number; total: number;
    duration_sec: number; spend: string; peak: number;
    /** What the day's calls sold for, and what the carrier charged us for the ones we sold. */
    sell: string; cost: string;
  }>;
  hours: Array<{ hour: number; total: number; answered: number }>;
  outcomes: Array<{ outcome: 'answered' | 'cancelled' | 'no_answer' | 'busy' | 'failed'; calls: number }>;
  causes: Array<{ cause: string | null; sip_code: number | null; direction: string; calls: number; last_at: string }>;
  quality: { measured: number; avg_mos: number | null; poor: number };
  peak: { calls: number; at: string } | null;
  registrations: Array<{ tenant_id: string; online: number; recent: number }> | null;
  trunks: Array<{
    id: string; name: string; carrier: string | null; account: string | null; account_slug: string | null;
    health: 'unknown' | 'up' | 'down' | 'degraded';
    registration: 'registered' | 'registering' | 'not_registered' | 'failing' | 'not_required' | 'unknown';
    uptime_24h_pct: number | null; latency_ms: number | null;
    outbound: number; inbound: number; answered: number;
  }> | null;
  fraud: { open_holds: number; alerts: number } | null;
  numbers: { assigned: number; available: number; other: number } | null;
  /** Phones online at the end of each day, from the switch's presence record; null before it starts. */
  registrations_by_day: Array<{ day: string; online: number | null }> | null;
  /**
   * Revenue against carrier cost over the window. Cost is counted only on calls we sold; a sold
   * call with no cost on record is `unpriced`, so the margin reads as "at least".
   */
  margin: {
    sell: string; cost: string; margin: string; margin_pct: number | null; sold_calls: number; unpriced: number;
    accounts: Array<{
      tenant_id: string; name: string; slug: string; sold_calls: number; unpriced: number;
      sell: string; cost: string; margin: string; margin_pct: number | null;
    }>;
  };
  /** Each switch's last heartbeat (node:read). */
  nodes: Array<{
    id: string; name: string; status: string;
    liveness: 'up' | 'down' | 'never'; last_heartbeat_at: string | null;
    active_calls: number; registrations: number;
    load_1: number | null; load_5: number | null;
    mem_pct: number | null; mem_used_mb: number | null; mem_total_mb: number | null;
    disk_pct: number | null; uptime_sec: number | null; cdr_spool_depth: number; agent_version: string | null;
    services: Record<string, string>;
    failing_assertions: string[];
    /** Null when the node has no Teams listener. `up` null when there is nothing to judge by. */
    teams: { up: boolean | null; down_since: string | null; proxies: Array<{ proxy: string; status: string; at: string }> } | null;
  }> | null;
  /** The cert-watch job's last reading, soonest to expire first (node:read). */
  certificates: { checked_at: string; items: Array<{ label: string; name: string | null; days_left: number | null; error: string | null }> } | null;
  security: {
    /** fail2ban's SIP bans at the agent's last snapshot; null without node:read or a node. */
    bans_now: number | null; bans_taken_at: string | null;
    /** The last seven days of refusals at the edge: failed sign-ins, and scanners, floods and rate limits. */
    refusals: Array<{ day: string; sign_ins: number; edge: number }> | null;
    /** Fraud holds placed and alerts raised per day over the window (fraud:read). */
    holds: Array<{ day: string; holds: number; alerts: number }> | null;
  };
  /** The email outbox as a queue (billing:read). */
  email: { waiting: number; retrying: number; failed_7d: number; last_failure: { at: string; error: string } | null } | null;
}
